import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useVenue } from '@/hooks/useVenues'
import { useVenueCalendar } from '@/hooks/useEvents'
import { VenueCalendar } from '@/components/VenueCalendar'
import { showsVenueContextHero } from '@/components/VenueContextHero'
import { useAuth } from '@/auth/AuthContext'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { currentMonth, gridRange, type MonthRef } from '@/lib/monthGrid'

/**
 * A venue's calendar: what is on, as a shape in time.
 *
 * It used to fill the venue's dashboard, until the dashboard became about how the
 * venue is doing (docs/gohighlevel-stats.spec.md, "The dashboard and the
 * calendar"). Here it has the page to itself, so a month of weekends gets the
 * width it needs.
 */
export function VenueCalendarPage() {
  const { venueId } = useParams()
  const id = venueId ?? ''
  const navigate = useNavigate()
  const { user } = useAuth()
  // Inside a venue the context band already names it; from outside, the header does.
  const bandShown = showsVenueContextHero(user)
  const [month, setMonth] = React.useState<MonthRef>(() => currentMonth())

  const venueQuery = useVenue(id)
  const grid = React.useMemo(() => gridRange(month), [month])
  const calendarQuery = useVenueCalendar({
    venueId: id,
    from: grid.from,
    to: grid.to,
  })

  if (venueQuery.isPending) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (venueQuery.isError || !venueQuery.data) {
    return (
      <PageContainer width="wide" className="space-y-3">
        <ErrorState
          message={venueQuery.isError ? requestErrorMessage(venueQuery.error) : 'That venue could not be loaded.'}
          onRetry={() => void venueQuery.refetch()}
        />
        <Link to="/venues/all" className="text-sm text-ink-muted underline underline-offset-2 hover:text-ink">
          Back to venues
        </Link>
      </PageContainer>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <PageHeader title="Calendar" meta={bandShown ? undefined : venueQuery.data.name} />
      {calendarQuery.isError ? <FormError>{requestErrorMessage(calendarQuery.error)}</FormError> : null}
      <VenueCalendar
        month={month}
        onMonthChange={setMonth}
        events={calendarQuery.data?.events ?? []}
        holds={calendarQuery.data?.holds ?? []}
        tracks={calendarQuery.data?.tracks ?? []}
        concurrentEvents={calendarQuery.data?.concurrentEvents ?? false}
        onSelectEvent={(eventId) => navigate(`/events/${eventId}`)}
        loading={calendarQuery.isFetching}
      />
    </PageContainer>
  )
}
